import { useMemo } from 'react'
import type { WorkspaceActivityRecord } from '../../../../services/activityService'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type {
  LiveModuleHeadline,
  LiveModuleRecord,
  LiveModuleSparkline,
} from '../LiveModuleCard'
import { bucketize, calculateDelta } from './bucketize'

export type UseAuditSignalsModuleParams = {
  workspaceId: string
  timeRange: AnalysisTimeRangeKey
  auditRecords: WorkspaceActivityRecord[]
  loading?: boolean
  error?: Error | null
  now?: Date
}

export const useAuditSignalsModule = ({
  timeRange,
  auditRecords,
  loading = false,
  error = null,
  now,
}: UseAuditSignalsModuleParams) => {
  return useMemo(() => {
    const timestamps = auditRecords.map((r) => r.createdAt)
    const points = bucketize(timestamps, timeRange, now)
    const delta = calculateDelta(points)

    // Group by (actor, nodeType)
    const countsByKey = new Map<string, { actor: string; targetType: string; count: number }>()

    for (const record of auditRecords) {
      const actor = record.actorName?.trim() || record.actorId || 'Workspace member'
      const targetType = record.nodeType || record.targetType || 'Event'
      const key = `${actor}::${targetType}`
      const existing = countsByKey.get(key)
      if (existing) {
        existing.count += 1
      } else {
        countsByKey.set(key, { actor, targetType, count: 1 })
      }
    }

    const records: LiveModuleRecord[] = Array.from(countsByKey.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((entry, index) => ({
        id: `audit-signal-${index}-${entry.actor}-${entry.targetType}`,
        primary: entry.actor,
        secondary: `${entry.targetType} activity`,
        numeric: entry.count,
        drillTarget: 'SUMMARY',
      }))

    const headline: LiveModuleHeadline = {
      value: auditRecords.length,
      delta,
      label: 'events',
    }

    const sparkline: LiveModuleSparkline = {
      points,
      direction: 'up-is-bad',
    }

    return {
      headline,
      sparkline,
      records,
      loading,
      error,
    }
  }, [auditRecords, timeRange, now, loading, error])
}
